
import React, { useEffect } from 'react';
import SEO from '@/components/SEO';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Package, BarChart3, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ProductManagement = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.reveal').forEach(el => {
      el.classList.add('opacity-0');
      observer.observe(el);
    });

    return () => {
      document.querySelectorAll('.reveal').forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  const pricingTiers = [
    {
      name: "Starter",
      description: "Up to 10K orders per month",
      prices: ["Base 500 EUR", "+200 EUR extended"],
      features: ["Full product reporting", "Sales & stock metrics", "Data organized in single dashboard", "Daily data freshness"]
    },
    {
      name: "Grower",
      description: "Up to 50K orders per month",
      prices: ["Base 850 EUR", "+300 EUR extended"],
      features: ["Everything in Starter", "PPC marketing data", "Website traffic integration", "Enhanced performance"]
    },
    {
      name: "Player",
      description: "Above 50K orders per month",
      prices: ["Base 1200 EUR", "+400 EUR extended"],
      features: ["Everything in Grower", "Priority support", "Custom data integrations", "Enhanced performance"]
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Product Management Automation | Stratodata"
        description="Automate ecommerce product management — from data enrichment to catalogue syndication. Keep your product data clean, consistent and up to date."
        canonical="/services/ecommerce-automation/product-management"
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-primary/5">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <Package className="h-4 w-4" />
                <span>Ecommerce Automation Suite</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Product Management Workstream
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                End-to-end product lifecycle management including catalog optimization, pricing strategies, and inventory monitoring for maximum profitability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => {
                  const section = document.getElementById('features');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}>
                  Explore Features
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => {
                  const section = document.getElementById('pricing');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}>
                  View Pricing
                </Button>
              </div>
            </div>
            <div className="reveal">
              <div className="relative bg-white p-8 rounded-2xl shadow-lg border border-border aspect-square flex items-center justify-center">
                <Package className="h-32 w-32 text-primary/30" />
                <div className="absolute -top-4 -right-4 bg-primary text-white text-sm font-medium px-4 py-2 rounded-full">
                  Core Workstream
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="reveal order-2 md:order-1">
              <div className="bg-secondary/30 p-8 rounded-2xl">
                <blockquote className="text-lg italic">
                  "Product management is responsible for what products are purchased into the company and also often in making sure that these products are sold. This workstream is responsible for keeping the stock levels optimal. Not too small, so there would be nothing to sell, but also not too high so the warehouse would be too full and too much cash would be held in it."
                </blockquote>
                <p className="mt-6 text-lg font-medium">
                  Success or failure of ecommerce retailers often depend on how well this workstream is managed. If this is done right, the retailer is growing and has the stocks well under control. If it gets out of control, it might bankrupt the company.
                </p>
              </div>
            </div>
            <div className="reveal order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                The Critical Balance of Inventory and Sales
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Product management is about finding the sweet spot and staying there as long as possible. It's a delicate balance between maintaining adequate stock levels to meet demand without tying up excessive capital.
              </p>
              <p className="text-lg text-muted-foreground">
                Our Product Management Suite provides the tools and insights needed to optimize this balance, helping you grow profitably while maintaining control over your inventory.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 md:py-24 bg-secondary/20">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Data Visibility
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Common Use Cases
            </h2>
            <p className="text-lg text-muted-foreground">
              Our experience shows these data visibility needs come up most frequently in product management:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 reveal">
            {[
              "What are our sales, gross margin and GM% per product, brand and product categories?",
              "What is our stock level per product, brand and product categories?",
              "For how long will our stock last based on current stock level and sellout tempo?",
              "How are we offering the product in marketing systems?",
              "How do our product perform on our website in terms of detail traffic and checkout process?",
              "What is the marketing profit of our products, brands and product categories? [Marketing profit = gross margin - marketing cost]",
              "How do our current prices compare to competitors?"
            ].map((useCase, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-border flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-base">{useCase}</p>
              </div>
            ))}
          </div>

          <div className="text-center reveal">
            <p className="text-lg font-medium mb-6">
              To answer these use cases, our Product Management Suite comes ready with powerful reporting solutions.
            </p>
            <Button size="lg" onClick={() => {
              const section = document.getElementById('features');
              section?.scrollIntoView({ behavior: 'smooth' });
            }}>
              Explore Our Solution
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Solution Features
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Product Management Suite
            </h2>
            <p className="text-lg text-muted-foreground">
              Our comprehensive solution combines data from multiple sources to provide clear visibility into your product performance and inventory status.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 reveal">
            <Card>
              <CardHeader>
                <CardTitle>Combined Metrics</CardTitle>
                <CardDescription>Sales & stock integration</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Combination of sales & stock metrics with core product management KPIs like Weeks on Hand (WOH) calculated automatically.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Flexible Catalog View</CardTitle>
                <CardDescription>Custom categorization</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Data presented on your own product catalogue with KPIs drillable on all levels, from EAN all the way to top category or brand.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Profitability Focus</CardTitle>
                <CardDescription>Beyond standard metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Marketing profitability as a key profit metric, showing true performance after accounting for promotional costs.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Channel Performance</CardTitle>
                <CardDescription>Web analytics integration</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Visibility on web and PPC data, helping you understand how products perform in different channels.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Competitive Analysis</CardTitle>
                <CardDescription>Market positioning</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Competitor prices included to help you understand your market position and optimize pricing strategies.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Unified Dashboard</CardTitle>
                <CardDescription>Daily refreshed data</CardDescription>
              </CardHeader>
              <CardContent>
                <p>All data organized in one management dashboard with daily data freshness for timely decision making.</p>
              </CardContent>
            </Card>
          </div>

          <div className="reveal">
            <h3 className="text-2xl font-semibold mb-6">Data Requirements</h3>
            
            <Tabs defaultValue="base">
              <TabsList className="mb-8">
                <TabsTrigger value="base">Base Tier</TabsTrigger>
                <TabsTrigger value="extended">Extended Tier</TabsTrigger>
                <TabsTrigger value="superior">Superior Tier</TabsTrigger>
              </TabsList>
              
              <TabsContent value="base" className="bg-white p-6 rounded-xl shadow-sm border border-border mb-8">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Orders, invoices, credit notes on order line level [ERP]</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Stock history, daily stock movements [ERP, stock management]</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Product catalogue [ERP / Google Feed / other]</span>
                  </li>
                </ul>
              </TabsContent>
              
              <TabsContent value="extended" className="bg-white p-6 rounded-xl shadow-sm border border-border mb-8">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>PPC marketing data [Google Ads, Bing]</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Website traffic [Google Analytics API or BigQuery]</span>
                  </li>
                </ul>
              </TabsContent>
              
              <TabsContent value="superior" className="bg-white p-6 rounded-xl shadow-sm border border-border mb-8">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Competitor prices per product [PPC scraper]</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Calculated vs competition benchmark</span>
                  </li>
                </ul>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-secondary/20">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Pricing Plans
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground">
              We offer flexible pricing options based on your business size and needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 reveal">
            {pricingTiers.map((tier, index) => (
              <Card key={index} className={index === 1 ? "border-primary shadow-md" : ""}>
                {index === 1 && (
                  <div className="bg-primary text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <CardHeader>
                  <CardTitle>{tier.name}</CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-6">
                    {tier.prices.map((price, i) => (
                      <p key={i} className={i === 0 ? "text-2xl font-bold" : "text-base"}>
                        {price}
                      </p>
                    ))}
                  </div>
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" variant={index === 1 ? "default" : "outline"}>
                    Get Started
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-border text-center reveal">
            <p className="text-lg font-medium mb-4">Superior tier: Contact us for pricing estimate</p>
            <Button variant="outline">
              Contact for Custom Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Adoption Section */}
      <section className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                Implementation
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                Adoption Phase
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Adoption phase ensures that your team gets maximum value from the delivered reporting solutions. Support in this step is often crucial to onboard all relevant team members on the solution, clearly define responsibilities and make sure that the presented data views are understood and accepted.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                It is very beneficial to the culture and overall success of the organization if the data is consumed and utilized on many levels of the organization, not just by the top management. We recommend going in the direction of democratization of data and wide access to analytical tools.
              </p>
              <p className="text-lg text-muted-foreground">
                In the specific case of product management, division of responsibilities is often done on top of brands or product segments for multiperson teams. This means that one colleague is responsible for a set of brands or categories, while another one for a different set.
              </p>
            </div>
            <div className="reveal">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-border mb-6">
                <h3 className="text-xl font-semibold mb-4">Weeks on Hands (WOH)</h3>
                <p className="mb-4">
                  WOH is often used as a core metric for management of stocks. Its power lies in the combination of current stock levels as well as the recent sellout tempo.
                </p>
                <div className="bg-secondary/30 p-4 rounded-lg mb-4">
                  <p className="font-mono">WOH 4 EUR = stock value EUR / average weekly sellout for last 4 weeks EUR</p>
                </div>
                <p>
                  This metric yields an estimated amount of weeks for which we have available stocks on the given level of aggregation. A product manager can drill into their area of responsibility and look for sources of overstock or understock.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <h3 className="text-xl font-semibold mb-4">Weekly Data-Driven Cycle</h3>
                <ol className="space-y-2 list-decimal pl-5">
                  <li>Evaluation of current situation</li>
                  <li>Identification of risks and opportunities</li>
                  <li>Planning of activities for the upcoming period, setting of due date</li>
                  <li>Application of the planned activities</li>
                  <li>Evaluation of effects when the activity has taken place</li>
                  <li>Repeat</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Automation Section */}
      <section className="py-16 md:py-24 bg-secondary/20">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Put Data to Work
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Automation Solutions
            </h2>
            <p className="text-lg text-muted-foreground">
              When we have all of the data which we need for the decision already available, the next step is to automate the actions.
            </p>
            <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border border-border text-left">
              <p className="italic text-lg mb-4">Our process begins with a simple question:</p>
              <blockquote className="border-l-4 border-primary pl-4 py-2 text-xl font-medium">
                "If you had all of the data available with infinite time and energy to make your decision, how would you decide?"
              </blockquote>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 reveal">
            <Card>
              <CardHeader>
                <CardTitle>Autopricing</CardTitle>
                <CardDescription>Custom pricing algorithms</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Automated pricing algorithm which sets prices of your products based on your business logic. Our approach is that we tailor the solution based on your pricing strategy - you tell us how you want to price!</p>
                <p className="font-medium mb-4">Example pricing strategies include:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Competition-based: If your price is lower than competition and already selling, increase the price just below competition to maximize margin while staying competitive.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Volume-based: If your price is higher than competition and volumes are low, gradually decrease price until traffic increases to find optimal pricing.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Recommender</CardTitle>
                  <CardDescription>Product recommendation engine</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>
                    Advanced recommendation algorithms to suggest related products and increase basket size. Personalized for each customer based on their browsing and purchase history.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Autostock</CardTitle>
                  <CardDescription>Inventory optimization</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>
                    Automated inventory management system that predicts optimal stock levels and triggers reorder points based on sales velocity, seasonality, and lead times.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="text-center reveal">
            <Button size="lg" onClick={() => {
              // Navigate back to main ecommerce page
              navigate('/services/ecommerce-automation');
            }}>
              Back to Ecommerce Automation Suite
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductManagement;
